/**
 * Палитра проекта.
 *
 * Значения перенесены из кода КАК ЕСТЬ, без сведения близких оттенков.
 * Это осознанно: приложение принято заказчиком, и схлопывание дублей
 * изменило бы внешний вид. Зато теперь видно, где дубли есть, — решение
 * по ним принимается отдельно.
 *
 * Заметные дубли на момент переноса:
 *   ink / inkHex            rgb(31, 10, 53) и #1f0a35 — один цвет, две записи
 *   brandAlt / brandAlt2    #724edc и #723edc — отличаются одной цифрой
 *   dangerBright / ...Alt   #ff4d4d и #ff4d4f — отличаются одной цифрой
 */
export const theme = {
  colors: {
    // Основной тёмный
    ink: "rgb(31, 10, 53)",
    inkHex: "#1f0a35",
    inkSoft: "rgba(31, 10, 53, 0.7)",
    inkMuted: "rgba(31, 10, 53, 0.5)",
    inkBorder: "rgba(31, 10, 53, 0.1)",
    inkSurface: "rgba(31, 10, 53, 0.05)",

    // Бренд
    brand: "rgb(114, 82, 220)",
    brandAlt: "#724edc",
    brandAlt2: "#723edc",
    brandDark: "#603cbc",
    brandDarker: "#5a3bba",
    brandDeep: "rgb(93, 68, 176)",
    brandStrong: "rgba(114, 82, 220, 0.5)",
    brandMedium: "rgba(114, 82, 220, 0.15)",
    brandSoft: "rgba(114, 82, 220, 0.1)",
    brandSubtle: "rgba(114, 82, 220, 0.05)",
    brandLight: "rgba(131, 102, 228, 0.6)",
    brandTransparent: "rgba(131, 102, 228, 0)",

    // Нейтральные
    white: "rgb(255, 255, 255)",
    whiteHex: "#fff",
    black: "rgb(0, 0, 0)",
    blackHex: "#000",
    canvas: "rgb(247, 248, 248)",
    /* Фон страницы. Отдельно от canvas: тот почти белый (контраст к белому
       1.03) и карточки на нём не отделяются. Здесь 1.16 — достаточно,
       чтобы белая карточка читалась как приподнятая, и не настолько,
       чтобы фон лез в глаза. */
    pageBg: "rgb(238, 240, 245)",
    surfaceMuted: "rgb(250, 250, 250)",
    border: "rgb(225, 224, 229)",
    borderStrong: "rgb(226, 226, 226)",
    borderLight: "rgb(235, 235, 235)",
    borderLighter: "rgb(229, 229, 229)",
    grayIcon: "rgb(143, 132, 154)",
    grayDisabled: "rgb(185, 185, 185)",
    grayText: "#333",
    grayLine: "#ccc",

    // Тени и наложения
    shadowSoft: "rgba(0, 0, 0, 0.06)",
    shadowMedium: "rgba(0, 0, 0, 0.1)",
    shadowStrong: "rgba(0, 0, 0, 0.5)",
    shadowHeavy: "rgba(0, 0, 0, 0.8)",
    overlayGray: "rgba(202, 202, 202, 0.25)",

    // Статусы
    danger: "rgb(242, 40, 98)",
    dangerBright: "#ff4d4d",
    dangerBrightAlt: "#ff4d4f",
    dangerText: "rgb(176, 48, 76)",
    success: "rgb(86, 189, 116)",
    successText: "#3d8c56",
    warning: "#ffc107",
    warningText: "#8a6500",
    warningMuted: "rgb(194, 134, 31)",
    warningMutedText: "#9a6718",

    // Оттенки бренда, встречавшиеся поодиночке в отдельных компонентах.
    // Сведены сюда как есть: близкие значения не схлопывались, чтобы
    // не менять внешний вид.
    brandLighter: "rgb(121, 90, 223)",
    brandPressed: "rgb(105, 71, 216)",
    brandPale: "rgb(144, 112, 245)",
    brandNear: "rgb(122, 92, 224)",
    brandSoftAlt: "rgb(134, 106, 229)",
    brandGlow: "rgb(189, 169, 255)",
    brandDeepPurple: "#5a0cad",
    brandTint: "#f1edfb",
    brandShadow: "rgba(115, 83, 221, 0.37)",

    // Красные и пурпурные акценты: подсветки, градиенты, состояния ошибки.
    dangerPink: "#e52c6d",
    dangerRed: "rgb(237, 0, 4)",
    dangerRedAlt: "rgb(234, 0, 4)",
    dangerMuted: "rgb(223, 90, 92)",
    dangerGlow: "rgb(255, 169, 170)",
    dangerAccent: "rgb(235, 0, 98)",
    dangerDeep: "rgb(211, 4, 90)",
    dangerRedDeep: "rgb(221, 0, 4)",
    magenta: "rgb(218, 0, 185)",
    magentaDark: "rgb(185, 0, 118)",
    magentaDarker: "rgb(167, 1, 107)",

    // Нейтральные оттенки, от почти белого к почти чёрному.
    surfaceFaint: "rgb(249, 249, 249)",
    surfaceCool: "#f7f8fa",
    surfaceGray: "rgb(243, 243, 243)",
    borderFaint: "#f0f0f0",
    borderSoft: "#e4e4e4",
    borderCool: "#e1e0e5",
    borderMuted: "rgb(225, 225, 225)",
    borderMedium: "#e0e0e0",
    borderDark: "#d0d0d0",
    grayMid: "rgb(165, 165, 165)",
    grayMidAlt: "rgb(163, 163, 163)",
    grayCool: "rgb(163, 167, 174)",
    graySlate: "rgb(139, 129, 150)",
    grayMuted: "#888",
    graySubtle: "#777",
    graySecondary: "#6c757d",
    grayStrong: "#666",
    grayDeep: "#555",
    inkSoftDark: "rgb(39, 39, 39)",
    inkNearBlack: "rgb(20, 20, 20)",
  },
  /**
   * Размеры интерфейса.
   *
   * До этого в проекте было 8 радиусов (4, 6, 8, 10, 12, 14.4, 16, 24)
   * и 10 высот кнопок. Разница между 4 и 6 или между 8, 10 и 12 глазом
   * не читается как замысел — читается как небрежность.
   *
   * Значения в design px: их принимает pxToRem, а общий --ui-scale
   * применяется уже после этого.
   */
  radii: {
    /** Мелкое: чекбоксы, теги, иконочные кнопки. */
    sm: 6,
    /** Элементы управления: кнопки, поля ввода, селекты. */
    md: 10,
    /** Карточки, таблицы, модальные окна. */
    lg: 16,
    /** Круг: аватары, переключатели. */
    pill: 999,
  },

  /** Базовая шкала расстояний. Произвольные числа остаются только у геометрии, зависящей от макета. */
  spacing: {
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
    xxxl: 40,
  },

  /**
   * Границы по назначению.
   *
   * В проекте было одиннадцать токенов границ, и почти все давали
   * контраст к белому от 1.1 до 1.5 — ниже порога различимости.
   * Три из них, rgb(224,224,224), rgb(226,226,226) и rgb(229,229,229),
   * отличались друг от друга на два деления и жили как отдельные
   * сущности.
   *
   * Назначений всего три, и требования у них разные.
   */
  borders: {
    /** Разделитель внутри блока: строки таблицы, пункты списка. */
    divider: "rgba(31, 10, 53, 0.08)",
    /** Контур карточки: отделяет её от фона страницы. */
    outline: "rgba(31, 10, 53, 0.16)",
    /**
     * Граница поля ввода. 3.0 : 1 к белому — минимум по WCAG 1.4.11
     * для границ элементов управления. Поле опознаётся именно
     * по ней: собственная заливка даёт к белому 1.03 и сама по себе
     * поле не показывает.
     */
    field: "rgba(31, 10, 53, 0.71)",
  },

  /**
   * Ступени тени.
   *
   * В проекте было 77 объявлений box-shadow и почти два десятка разных
   * сочетаний смещения с размытием: 4/6, 4/8, 4/10, 4/12, 4.8/14.4,
   * 9/14.5, 2/6/29, 2.4/7.2/34.8. Разницу между 4/10 и 4/12 глаз
   * не видит — она читается не как замысел, а как небрежность.
   *
   * Три ступени по расстоянию до поверхности плюс контурное кольцо,
   * которым отделяются карточки. Значения готовые, подставляются
   * целиком: box-shadow: ${({ theme }) => theme.shadows.md};
   */
  shadows: {
    /** Приподнято чуть-чуть: наведение, мелкие элементы. */
    sm: "0 0.2rem 0.4rem rgba(0, 0, 0, 0.06)",
    /** Карточки, выпадающие списки, подсказки. */
    md: "0 0.4rem 1.2rem rgba(0, 0, 0, 0.1)",
    /** Оторвано от страницы: модальные окна. */
    lg: "0 1.2rem 3.2rem rgba(0, 0, 0, 0.14)",
    /** Контур вокруг карточки вместо границы. */
    ring: "0 0 0 0.6rem rgba(31, 10, 53, 0.05)",
  },

  /**
   * Три канонические высоты элементов управления.
   * При --ui-scale: 0.8 они визуально дают 32 / 40 / 48 CSS px.
   */
  controlHeight: {
    sm: 40,
    md: 50,
    lg: 60,
    /** 55 design px превращаются в минимальные 44 CSS px для касания. */
    touch: 55,
  },

  /**
   * Типографическая шкала.
   *
   * В проекте было 28 разных кеглей: 8, 10, 12, 13, 14, 14.4, 15, 16,
   * 16.8, 17, 18, 19.2, 20, 21.6, 22, 23, 24, 26, 28, 29, 32, 33.6, 34,
   * 36, 42, 48, 56, 290. Шаги 16 → 17 → 18 → 19.2 глаз не различает —
   * это не шкала, а «сколько получилось». Дробные значения пришли
   * пересчётом из макета.
   *
   * Здесь шесть ступеней для текста интерфейса плюс одна витринная —
   * для крупных цифр в часах, ценах и счётчиках. Значения в design px,
   * их принимает pxToRem.
   */
  fontSize: {
    /** Подписи, служебные пометки. */
    xs: 12,
    /** Мелкий текст: ячейки таблиц, вторичные пояснения. */
    sm: 14,
    /** Основной текст. */
    md: 16,
    /** Подзаголовки внутри блока. */
    lg: 20,
    /** Заголовок раздела. */
    xl: 24,
    /** Заголовок страницы. */
    xxl: 32,
    /** Витринные цифры: часы, цена тарифа, крупные счётчики. */
    display: 48,
  },

  fonts: {
    // Golos Text загружается глобально через version-pinned Fontsource CSS.
    primary: '"Golos Text Variable", sans-serif',
    monospace: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
  },
} as const;

export type AppTheme = typeof theme;
